import axios from 'axios'
import LoadingView from 'components/Elements/LoadingView'
import StyledLoader from 'components/Elements/StyledLoader'
import SectionTitle from 'components/SectionTitle'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import axiosClient from 'services/axios-client'
import {
  useBookQuery,
  useBookQuestionsQuery,
  useExistingPreviewQuery,
} from 'store/queries'
import { BookPreviewContainer } from './style'

function BookPreview() {
  const { bookId } = useParams()
  const { t } = useTranslation()
  const socket = useRef(null)

  const { data: book } = useBookQuery(bookId)
  const { data: questions } = useBookQuestionsQuery(bookId)
  const hasStory = useMemo(
    () => questions?.some((q) => q.answers.some((a) => a.is_submitted)),
    [questions],
  )
  const { isSuccess, data: existingPreview } = useExistingPreviewQuery(bookId)

  const [isGenerating, setIsGenerating] = useState(false)

  const downloadPdf = useCallback(
    async (fileInfo) => {
      axios
        .get(fileInfo.fileUrl ? fileInfo.fileUrl : existingPreview.fileUrl, {
          responseType: 'blob',
        })
        .then((res) => {
          const downloadLink = document.createElement('a')
          const pdfBlob = new Blob([res.data], { type: 'application/pdf' })
          downloadLink.href = URL.createObjectURL(pdfBlob)
          downloadLink.download = 'document.pdf'
          downloadLink.click()
          URL.revokeObjectURL(downloadLink.href)

          setIsGenerating(false)
        })
    },
    [existingPreview?.fileUrl],
  )

  const startBookGeneration = async () => {
    if (socket.current?.readyState === WebSocket.OPEN) {
      socket.current?.send(
        JSON.stringify({
          action: 'startPdf',
        }),
      )

      setIsGenerating(true)
    }
  }

  const startPdfGeneration = useCallback(
    async (socket_id) => {
      await axiosClient.post(`PDF/book/${bookId}/generate`, { socket_id })
    },
    [bookId],
  )

  const onConnectSocket = () => {
    try {
      if (socket.current?.readyState !== WebSocket.OPEN) {
        socket.current = new WebSocket(process.env.REACT_APP_WEBSOCKET_URL)
        socket.current.addEventListener('open', onSocketOpen)
        socket.current.addEventListener('close', onSocketClose)
        socket.current.addEventListener('message', (event) => {
          onSocketMessage(event.data)
        })
      }

      return () => {
        console.log('Cleaning up WS connection')
        socket.current.close()
      }
    } catch (err) {
      console.log(err)
    }
  }

  // TODO: Remove all the socket stuff and replace with polling.
  const onSocketOpen = useCallback(() => {
    console.log('Socket opened')
    socket.current?.send(
      JSON.stringify({
        action: 'getConnectionId',
      }),
    )
    const startPing = () => {
      if (socket.current?.readyState === WebSocket.OPEN) {
        socket.current?.send(
          JSON.stringify({
            action: 'sendPing',
          }),
        )

        setTimeout(() => {
          startPing()
        }, 25000)
      }
    }
    startPing()
  }, [])

  const onSocketClose = useCallback(() => {
    console.log('socket closed')
  }, [])

  const onSocketMessage = useCallback(
    (data) => {
      const _data = JSON.parse(data)

      switch (_data.action) {
        case 'socket_connected':
          break
        case 'book_generated':
          downloadPdf(_data.fileInfo)
          break
        case 'pong':
          break
        case 'start_pdf':
          startPdfGeneration(_data.socketId)
          break
        default:
          break
      }
    },
    [downloadPdf, startPdfGeneration],
  )

  useEffect(() => onConnectSocket(), []) // eslint-disable-line

  if (!isSuccess) return <StyledLoader />
  return (
    <BookPreviewContainer className="section-container">
      <div className="pdf-container modal-custom-body p20">
        <SectionTitle>{t('preview_book')}</SectionTitle>
        <p>
          {!existingPreview
            ? 'This will generate a pdf preview of your book. We use a smaller resolution of your photos than your final book in order to make the preview easier to manage.'
            : `You currently have a preview created on ${moment(
                new Date(existingPreview.LastModified),
              ).format('DD/MM/YYYY')}`}
        </p>
        <p>
          {!existingPreview
            ? 'This process can take up to 10 minutes based on the number of pages and images you have. Do you wish to continue?'
            : 'Generating a pdf preview of your book can take up to 10 minutes based on the number of pages and images you have.'}
        </p>
        {existingPreview && (
          <div className="note">
            <span>
              Note: PDF previews of your book will contain photos with less
              resolution than your final book to make the preview easier to
              manage.
            </span>
          </div>
        )}
        {book?.order?.name === 'Basic' && (
          <p>
            Just a reminder, the package you ordered was our Basic Package. This
            includes photos in black and white. Should you wish to upgrade that
            to photos in colour, you can do so before submitting your book to
            print.
          </p>
        )}
      </div>

      <div className="pdf-container modal-custom-footer">
        <div className="actions">
          {isGenerating ? (
            <LoadingView isLoading={true} />
          ) : (
            <>
              {existingPreview && (
                <Button className="btn-white btn-dash" onClick={downloadPdf}>
                  Download recent preview
                </Button>
              )}

              {hasStory ? (
                <Button
                  className="btn-blue btn-dash"
                  onClick={startBookGeneration}
                >
                  Generate new preview
                </Button>
              ) : (
                <p>Add some content to your book to generate a preview.</p>
              )}
            </>
          )}
        </div>
      </div>
    </BookPreviewContainer>
  )
}

export default BookPreview
